import { useCallback } from 'react'
import mParticleSdk, { SDKEventCustomFlags } from '@mparticle/web-sdk'

import useConfig from '../../config/state/use-config'
import useOrder from '../../order/state/order/use-order'

import { useInternalAnalytics } from '../../../utils/internalAnalytics'
import { useMParticleEventProperties } from './useMParticleEventProperties'

import { useMParticleGlobalProperties } from '../../../utils/internalAnalytics/internalAnalyticsUtils'
import { PaymentMethod } from 'models/ChargeIntent'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'

type TrackPurchaseEventFn = (
  transactionId: number,
  customAttributes?: mParticleSdk.SDKEventAttrs,
  customFlags?: SDKEventCustomFlags,
) => void

function useTrackPurchseEvent(): TrackPurchaseEventFn {
  const config = useConfig()
  const order = useOrder()
  const mParticleEventProperties = useMParticleEventProperties()

  const walletType = useSelector((state: RootState) => state.order.stripePaymentRequest.walletType)

  const { mParticle, consentSynced } = useInternalAnalytics()
  const mParticleGlobalProperties = useMParticleGlobalProperties()

  return useCallback(
    (transactionId, customAttributes, customFlags) => {
      if (!mParticle || !consentSynced || !mParticleGlobalProperties) {
        return
      }

      mParticle.eCommerce.setCurrencyCode(order.currency.value)

      // "Donation", campaign_id, amount, 1, undefined, "other", undefined, undefined, undefined
      const product = mParticle.eCommerce.createProduct(
        'Donation',
        config.campaignId,
        order.amount.value,
        1,
        undefined,
        'other',
        undefined,
        undefined,
        undefined,
        { frequency: order.frequency.value },
      )

      const customAttributesWithCommonProperties: mParticleSdk.SDKEventAttrs = {
        ...mParticleGlobalProperties,

        ...customAttributes,

        npo_id: mParticleEventProperties.npo_id,
        npo_name: mParticleEventProperties.npo_name,
        campaign_id: mParticleEventProperties.campaign_id,

        ...mParticleEventProperties.donation_components,
        ...mParticleEventProperties.donation_selections,

        fees: order.dcf.value.fee,
        donation_payment_method: getPaymentMethod(order.paymentDetails.type, walletType),
      }

      const customFlagsWithCommonFlags = {
        ...customFlags,

        'GA4.Title': document.title,
        'GA4.Location': document.location.href,
        'GA4.Referrer': document.referrer,
      }

      const customTransactionAttributes: mParticleSdk.TransactionAttributes = {
        Id: transactionId,
        Revenue: order.amount.value,
        Tax: 0,
        Shipping: '0.00',
      }

      mParticle.eCommerce.logProductAction(
        mParticleSdk.ProductActionType.Purchase,
        [product],
        customAttributesWithCommonProperties,
        customFlagsWithCommonFlags,
        customTransactionAttributes,
      )

      // * Set a session storage value after mparticle purchase event
      sessionStorage.setItem(`mparticle_purchase_${transactionId}_sent`, 'true')
    },
    [
      mParticle,
      consentSynced,
      mParticleEventProperties,
      mParticleGlobalProperties,
      config.campaignId,
      order.amount.value,
      order.dcf.value.fee,
      order.currency.value,
      order.frequency.value,
      order.paymentDetails.type,
      walletType,
    ],
  )
}

const getPaymentMethod = (paymentMethod: PaymentMethod, walletType: string | null) => {
  switch (paymentMethod) {
    case PaymentMethod.CC:
      return 'card'
    case PaymentMethod.Stripe:
      return 'card'
    case PaymentMethod.ACH:
      return 'ach'
    case PaymentMethod.PayPal:
      return 'paypal'
    case PaymentMethod.PayPal_Commerce:
      return 'paypal'
    case PaymentMethod.Plaid:
      return 'plaid'
    case PaymentMethod.Venmo:
      return 'venmo'

    case PaymentMethod.DW: {
      if (walletType === 'APPLEPAY') return 'apple'
      if (walletType === 'GPAY') return 'google'
      else return 'other'
    }

    default:
      return 'other'
  }
}

export default useTrackPurchseEvent
